/**
 * Detail collapse for a field event.
 */
import * as React from 'react';
import { Calendar, Clock, Building2, Users } from 'lucide-react';
import { LabelBadge } from '@/components/common/LabelBadge';
import { EVENT_STATUS_CONFIG } from '@/components/events/event-configs';
import { groupEventAttendanceParticipants } from '@/components/events/event-form-utils';
import {
  formatEventDate,
  formatEventTime,
  getAttendanceDisplay,
} from '@/components/events/event-table-utils';
import type { EventWithParticipants } from '@/types/event.types';

/** Props for {@link EventDetailsContent}. */
interface EventDetailsContentProps {
  event: EventWithParticipants;
}

/** Renders the expanded-row collapse body from a fetched event. */
function EventDetailsContent({ event }: Readonly<EventDetailsContentProps>) {
  const statusConfig = EVENT_STATUS_CONFIG[event.status];
  const dateLabel = formatEventDate(event.eventDate, 'EEEE d MMMM yyyy');

  const { organizers, students } = groupEventAttendanceParticipants(event.eventParticipants ?? []);
  /** Walk-in rows stored separately from registered `eventParticipants`. */
  const unregisteredStudents = event.eventUnregisteredParticipants || [];

  return (
    <div className="space-y-6 p-6">
      <div>
        <div className="flex items-center gap-2 mb-2">
          <LabelBadge
            label={statusConfig.label}
            icon={
              statusConfig.isInProgress ? <span className="session-dot-pulse" /> : statusConfig.icon
            }
            variant={statusConfig.badgeVariant}
            color={statusConfig.badgeColor}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="flex items-start gap-3">
          <Calendar className="w-5 h-5 text-muted-foreground mt-0.5" />
          <div>
            <div className="font-medium">Date</div>
            <div className="text-sm text-muted-foreground capitalize">{dateLabel}</div>
          </div>
        </div>

        <div className="flex items-start gap-3">
          <Clock className="w-5 h-5 text-muted-foreground mt-0.5" />
          <div>
            <div className="font-medium">Horaire</div>
            <div className="text-sm text-muted-foreground">
              {formatEventTime(event.startTime)} - {formatEventTime(event.endTime)}
            </div>
          </div>
        </div>

        {event.establishmentName && (
          <div className="flex items-start gap-3">
            <Building2 className="w-5 h-5 text-muted-foreground mt-0.5" />
            <div>
              <div className="font-medium">Établissement</div>
              <div className="text-sm text-muted-foreground">{event.establishmentName}</div>
            </div>
          </div>
        )}

        {event.activityName && (
          <div className="flex items-start gap-3">
            <Users className="w-5 h-5 text-muted-foreground mt-0.5" />
            <div>
              <div className="font-medium">Activité</div>
              <div className="text-sm text-muted-foreground">{event.activityName}</div>
            </div>
          </div>
        )}
      </div>

      {organizers.length > 0 && (
        <div>
          <h3 className="font-semibold mb-3">Organisateurs ({organizers.length})</h3>
          <div className="space-y-2">
            {organizers.map((p) => {
              const attendance = getAttendanceDisplay(p.attendanceStatus);
              return (
                <div key={p.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded">
                  <div>
                    <div className="font-medium">
                      {p.user?.firstName} {p.user?.lastName}
                    </div>
                    <div className="text-sm text-muted-foreground capitalize">{p.role}</div>
                  </div>
                  {p.attendanceStatus && (
                    <div className="ml-auto">
                      <span className={`text-xs px-2 py-1 rounded ${attendance.className}`}>
                        {attendance.label}
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {students.length > 0 && (
        <div>
          <h3 className="font-semibold mb-3">Étudiants inscrits ({students.length})</h3>
          <div className="space-y-2">
            {students.map((p) => {
              const attendance = getAttendanceDisplay(p.attendanceStatus);
              return (
                <div
                  key={p.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded"
                >
                  <div className="font-medium">
                    {p.user?.firstName} {p.user?.lastName}
                  </div>
                  {p.attendanceStatus && (
                    <span className={`text-xs px-2 py-1 rounded ${attendance.className}`}>
                      {attendance.label}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {unregisteredStudents.length > 0 && (
        <div>
          <h3 className="font-semibold mb-3">
            Participants non inscrits ({unregisteredStudents.length})
          </h3>
          <div className="space-y-2">
            {unregisteredStudents.map((p) => {
              const attendance = getAttendanceDisplay(p.attendanceStatus);
              return (
                <div
                  key={p.id}
                  className="flex items-center justify-between p-3 bg-blue-50 rounded"
                >
                  <div>
                    <div className="font-medium">
                      {p.firstName} {p.lastName}
                    </div>
                    <div className="text-sm text-muted-foreground">{p.primaryContact}</div>
                  </div>
                  {p.attendanceStatus && (
                    <span className={`text-xs px-2 py-1 rounded ${attendance.className}`}>
                      {attendance.label}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {event.internalNotes && (
        <div>
          <h3 className="font-semibold mb-2">Notes internes</h3>
          <p className="text-sm text-muted-foreground whitespace-pre-wrap">{event.internalNotes}</p>
        </div>
      )}
    </div>
  );
}

/** Detail renderer for an event ({@link DataTable} expanded-row collapse). */
export function renderEventDetails(event: EventWithParticipants): React.ReactNode {
  return <EventDetailsContent event={event} />;
}
